import React, { useState } from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Divider } from '@mui/material';
import color from 'src/utils/colors';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';
import { useLibraryExport, ExportOptionId } from './hooks/useLibraryExport';
import { useSnackbar } from 'src/utils/Hooks';

interface ExportOption {
  id: ExportOptionId;
  label: string;
}

const EXPORT_OPTIONS: ExportOption[] = [
  { id: 'exportAll', label: 'All content' },
  { id: 'exportGenerated', label: 'Master content' },
  { id: 'exportUploaded', label: 'Draft content' },
  { id: 'exportCurrent', label: 'Current content' }
];

interface LibraryExportModalProps {
  open: boolean;
  onClose: () => void;
}

const OTHER_OPTIONS: ExportOptionId[] = ['exportGenerated', 'exportUploaded', 'exportCurrent'];

export const LibraryExportModal = ({ open, onClose }: LibraryExportModalProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Set<ExportOptionId>>(new Set());
  const { handleExport, isExporting } = useLibraryExport();
  const { showSnackbar } = useSnackbar();

  const isAllContentSelected = selectedOptions.has('exportAll');

  const handleClose = () => {
    setSelectedOptions(new Set());
    onClose();
  };

  const handleClear = () => {
    setSelectedOptions(new Set());
  };

  const handleExportClick = async () => {
    if (selectedOptions.size === 0) return;

    try {
      const exportTypes = Array.from(selectedOptions);
      await handleExport(exportTypes);
      showSnackbar({ type: 'success', message: 'Content exported successfully.' });
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export content.';
      showSnackbar({ type: 'error', message });
    }
  };

  const handleOptionChange = (optionId: ExportOptionId) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      
      if (optionId === 'exportAll') {
        if (next.has('exportAll')) {
          next.clear();
        } else {
          next.add('exportAll');
          OTHER_OPTIONS.forEach((id) => next.add(id));
        }
      } else {
        if (next.has(optionId)) {
          next.delete(optionId);
          next.delete('exportAll');
        } else {
          next.add(optionId);
          if (OTHER_OPTIONS.every((id) => next.has(id))) {
            next.add('exportAll');
          }
        }
      }
      
      return next;
    });
  };

  const isOptionDisabled = (optionId: ExportOptionId) => {
    return optionId !== 'exportAll' && isAllContentSelected;
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': {
          width: '450px',
          padding: '18px 24px 16px 24px',
          borderRadius: '8px'
        },
        '& .genericModal__footer': {
          position: 'relative',
          padding: 'unset',
          paddingTop: '16px',
          borderTop: 'none'
        },
        '& .genericModal__body': {
          gap: '8px',
          paddingTop: '24px',
          height: '372px'
        }
      }}
    >
      <Flex alignItems="center" className="genericModal__header">
        <Text
          variant="h4"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto',
            fontWeight: 700 
          }}
        >
          Export
        </Text>
        <CloseIcon
          style={{ cursor: 'pointer', width: '16px', height: '16px', position: 'absolute', right: '24px', top: '24px' }}
          onClick={handleClose}
        />
      </Flex>

      <Divider
        sx={{
          marginTop: '16px',
          position: 'relative',
          left: '-24px',
          width: 'calc(100% + 48px)'
        }}
      />

      <GenericModal.Body>
        <Text
          variant="body2"
          sx={{
            marginBottom: '24px'
          }}
       >
          Select the type of content you would like to export.
        </Text>
        <Flex flexDirection="column" gap={16}>
          {EXPORT_OPTIONS.map((option) => {
            const isDisabled = isOptionDisabled(option.id);
            return (
              <Flex key={option.id} alignItems="center" gap={8}>
                <StyledCheckbox
                  checked={selectedOptions.has(option.id)}
                  onChange={() => handleOptionChange(option.id)}
                  disabled={isDisabled}
                  iconProps={{ width: '14px', height: '14px' }}
                />
                <Text variant="body2" sx={{ color: color.blueGrey }}>
                  {option.label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </GenericModal.Body>

      <Divider
        sx={{
          position: 'relative',
          left: '-24px',
          width: 'calc(100% + 48px)'
        }}
      />

      <GenericModal.Footer>
        <Flex justifyContent="space-between">
          <SlButton onClick={handleClear} disabled={selectedOptions.size === 0}>
            Clear
          </SlButton>
          <SlButton
            variant="contained"
            onClick={handleExportClick}
            disabled={selectedOptions.size === 0 || isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </GenericModal>
  );
};
